












import LoaderReport from "@/components/utility/LoaderReport.vue";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { reportHomeQueryParams } from "@/lib/queryParams/reportHomeQueryParams";
import { thgQueryParams } from "@/lib/queryParams/thgQueryParams";
import { AppColorSchema } from "@/lib/utility/AppColorSchema";
import { LocalHostToPartnerMapper } from "@/lib/utility/local-host-to-partner-mapper";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportType } from "@/store/enum/reportType";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { SettingModule } from "@/store/modules/setting.store";
import { Component, Vue } from "vue-property-decorator";
import { NotificationModule } from "@/store/modules/notification.store";
import eventBus from "@/lib/eventBus";
import { CustomerAccountMeModule } from "@/store/modules/customer-account-me.store";
import LanguageDialog from "@/components/utility/LanguageDialog.vue";
import { UserModule } from "@/store/modules/me-user.store";
import AttachmentRequestContinueDialog from "@/views/report/AttachmentRequestContinueDialog.vue";
import { AttachmentResponseModule } from "@/store/modules/attachment-response.store";
import { AttachmentRequestStatusEnum } from "@/lib/enum/attachment-request-status.enum";
import { FleetManagerModule } from "@/store/modules/fleet/manager-fleet.store";
import { VehicleFleetModule } from "@/store/modules/fleet/vehicle-fleet.store";
import { DamageFleetModule } from "@/store/modules/fleet/damage-fleet.store";
import { InvoiceFleetModule } from "@/store/modules/fleet/invoice-fleet.store";
import { SUPPORT_LOCALES } from "@/plugins/I18nPlugin";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";

@Component({
  components: { LoaderReport, LanguageDialog, AttachmentRequestContinueDialog }
})
export default class V extends Vue {
  isLanguageDialog = false;
  isOngoingAttachmentRequestActive = false;

  created() {
    this.trySetStepperByPath();

    ConfigModule.setNavigationSettings({
      showMmmmintLogo: false,
      showTheReportList: false,
      showThePartnerList: false,
      showTheCustomerList: false,
      showTheOrganizationList: false,
      showTheFleetList: false,
      showTheAnonymizer: false,
      showTheOnlineBookingList: false,
      showTheDigitalSignatureList: false,
      showTheAdminList: false,
      showTheThgList: false,
      showTheAboutList: true,
      showThgAboutList: false,
      showThgFeatureSettings: false,
      showTheProjectList: false,
      showTheCostList: false
    });

    if (FeatureModule.onlineBooking) {
      ConfigModule.setNavigationSettings({
        showMmmmintLogo: true,
        showTheReportList: true
      });
    }
  }

  async mounted() {
    ReportModule.setLoading(true);
    ConfigModule.setAppContext(AppContextEnum.REPORT);
    new AppColorSchema(this.$vuetify, ConfigModule.damageReportColorSchema).setColors();

    try {
      await LocalHostToPartnerMapper.tryGetPartnerByHost(window.location.hostname);
    } catch (error) {
      this.$log.error(error);
      this.$toast("Fehler beim Laden der Anwendung.");
      this.$router.push({ name: "PartnerContactError" });
    } finally {
      ReportModule.setLoading(false);
    }

    await this.handlePartnerSettings();

    try {
      // if some data is given in the query the report is prefilled.
      this.prefillDataFromQuery();
    } catch (error) {
      Vue.$log.error(error);
    }

    this.checkOngoingAttachmentRequest();

    eventBus.$on("logout", () => {
      NotificationModule.setIsNotificationsLoaded(false);

      FleetManagerModule.reset();
      VehicleFleetModule.reset();
      DamageFleetModule.reset();
      InvoiceFleetModule.reset();

      this.$log.info("Handle Logout Event(Redirect to Login)");
      this.$router.push({ name: "Logout" });
    });

    eventBus.$on("login", async () => {
      /**
       * Create customer account if it does not exist
       */
      try {
        await CustomerAccountMeModule.create({});

        if (this.distinctPartnerFleetIds && this.distinctPartnerFleetIds.length) {
          const promises: Promise<any>[] = [];

          promises.push(
            FleetManagerModule.getFleetTree({
              partnerId: this.partnerId,
              fleetId: this.distinctPartnerFleetIds[0]
            })
          );

          await Promise.all(promises);

          await this.$router.push({ name: "FleetAnalyticsView" });
        }
      } catch (error) {
        Vue.$log.error(error);
      }
    });
  }

  /**
   * If the user has an ongoing attachment request in the store, checking if it is still valid.
   * User may have refreshed the page during an attachment response, as it would have been cleared on submit otherwise.
   * If it is still valid, offer the user to continue it.
   */
  async checkOngoingAttachmentRequest() {
    const routeNames = ["ReportDataResponse", "ReportDataResponseAttachmentId"];
    if (routeNames.includes(this.$router.currentRoute.name ?? "")) {
      // If we are already in the attachment response, we don't need to check for ongoing requests.
      return;
    }

    this.$log.debug("Checking for ongoing attachment request in store...");
    const partnerId = ReportModule.partner?._id || PartnerModule.partner?._id;
    const accessToken = AttachmentResponseModule.attachmentRequestToken;
    if (partnerId && accessToken) {
      try {
        const attachmentResponse = await AttachmentResponseModule.find({
          partnerId,
          accessToken
        });

        if (!attachmentResponse) {
          this.$log.debug("User had an ongoing attachment request in store, but it could not be found.");
          AttachmentResponseModule.clearOngoingAttachmentRequest();

          return;
        }

        if (attachmentResponse.status === AttachmentRequestStatusEnum.COMPLETED) {
          this.$log.debug("User had an ongoing attachment request in store, but it is already completed.");
          AttachmentResponseModule.clearOngoingAttachmentRequest();

          return;
        }

        // If we reach this point, the attachment request is still ongoing and valid.
        // We can offer the user to continue it.
        this.$log.debug("Offer the user continue the attachment request.");
        this.isOngoingAttachmentRequestActive = true;
      } catch (error) {
        this.$log.error("Error fetching attachment request by id from query param");
        this.$log.error(error);
        AttachmentResponseModule.clearOngoingAttachmentRequest();
      }
    } else {
      this.$log.debug("No ongoing attachment request found in store.");
    }
  }

  get loading() {
    return ReportModule.loading;
  }

  get fleetPartnerPermission() {
    if (this.partnerId) {
      return UserModule.fleetPermission.filter(p => this.partnerId === p.partnerId);
    }

    return UserModule.fleetPermission;
  }

  /**
   * Distinct list of fleetId found in fleet permission for current partner.
   */
  get distinctPartnerFleetIds() {
    return [...new Set(this.fleetPartnerPermission.map(p => p.fleetId))];
  }

  get partnerId() {
    return ReportModule.partner._id;
  }

  trySetStepperByPath() {
    const route = this.$route.path;
    const parts = route.split("/"); // "/3" -> ["", "3" ]
    const maybeAStep: string = parts[1];
    if (Object.values(ReportScreenEnum).includes(maybeAStep as ReportScreenEnum)) {
      ReportModule.setStep(maybeAStep as ReportScreenEnum);
    }
  }

  async handlePartnerSettings() {
    this.isLanguageDialog =
      !UserModule.isAuthenticated &&
      !ConfigModule.locale &&
      (ReportModule.partner.settings?.isLanguageSelectionActive ?? false);

    if (SUPPORT_LOCALES.includes(ReportModule.partner.language as LanguageCodeEnum)) {
      this.$i18n.locale = ConfigModule.locale ?? ReportModule.partner.language.toLowerCase() ?? this.$i18n.locale;
    } else {
      this.$i18n.locale = ConfigModule.locale ?? this.$i18n.locale;
    }

    this.$log.info(`Set default lang ${this.$i18n.locale}`);

    try {
      const partnerId = ReportModule.partner?._id || PartnerModule.partner?._id;

      if (!partnerId) {
        this.$log.error("No partnerId given when trying to load the partner's settings");

        return;
      }

      await SettingModule.getPublicSettingsByPartnerId(partnerId);
    } catch (error) {
      this.$log.error(error);
    }

    try {
      const reportType = await SettingModule.findSettingsInPartnerSettings("REPORT_TYPE");
      if (reportType) {
        ReportModule.setReportType(reportType as ReportType);
      }
    } catch (error) {
      this.$log.error(error);
      this.$log.warn("Error during setting report type");
    }

    try {
      const activeReportTypes = await SettingModule.findSettingsInPartnerSettings("ACTIVE_REPORT_TYPES");
      if (activeReportTypes) {
        const activeReprotTypes = JSON.parse(activeReportTypes);
        if (activeReprotTypes.length) {
          ReportModule.setActiveReportTypes(activeReprotTypes as ReportType[]);
        }
      }
    } catch (error) {
      this.$log.error(error);
      this.$log.warn("Error during setting active report types");
    }

    if (
      ReportModule.partner.settings?.themes?.light?.palette &&
      ReportModule.partner.settings?.themes?.light?.palette.length
    ) {
      ConfigModule.damageReportColor.palette = ReportModule.partner.settings?.themes?.light?.palette;
    }
  }

  prefillDataFromQuery() {
    reportHomeQueryParams.concat(thgQueryParams).forEach(param => {
      const value = this.$route.query[param.key];

      if (value) {
        param.onFound((value as string) || "");
        this.$log.info(`${param.key} set in store`);
      }
    });
  }
}
